import type { UI } from '@/v2/types';
import { useMemo } from 'react';

import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import removeMatchingOffers from './utils/removeMatchingOffers';

type UseFlightCampaignAvailabilityProps = {
  flightPromoCodes: Set<string> | undefined;
  roomOptions: {
    main: UI.RoomOption[] | undefined | null;
    other: UI.RoomOption[] | undefined | null;
    all: UI.RoomOption[] | undefined;
  };
  campaign: UI.Campaign | undefined | null;
};

const useFlightCampaignAvailability = ({
  flightPromoCodes,
  roomOptions,
  campaign,
}: UseFlightCampaignAvailabilityProps) => {
  const { searchQuery } = useSearchQuery();
  const { packageOption } = searchQuery || {};
  const promoCode = campaign?.sashing?.flightPromoCode;
  const title = campaign?.sashing?.flightPillText;

  // If we don't have a promo code and title for the campaign then nothing is valid
  const isCampaignValid = !!title && !!promoCode;

  const showCampaignPackageOptionVariants =
    isCampaignValid && !searchQuery?.noCampaign;

  const isCampaignAvailable =
    isCampaignValid && (!flightPromoCodes || flightPromoCodes.has(promoCode));

  const { campaignRoomOptions, nonCampaignAltRoomOption } = useMemo(() => {
    // If we are showing campaign options but there are no flights for the campaign,
    // AND we are not looking at best available rates =>
    // then we make all rooms unavailable (by setting matchingOffer to undefined).
    if (
      showCampaignPackageOptionVariants &&
      !isCampaignAvailable &&
      packageOption !== BEST_RATE_ID
    ) {
      return {
        campaignRoomOptions: {
          all: roomOptions.all?.map(removeMatchingOffers),
          main: roomOptions.main?.map(removeMatchingOffers),
          other: roomOptions.other?.map(removeMatchingOffers),
        },
        nonCampaignAltRoomOption: roomOptions.main?.[0],
      };
    }
    return {
      campaignRoomOptions: roomOptions,
      nonCampaignAltRoomOption: undefined,
    };
  }, [
    isCampaignAvailable,
    packageOption,
    roomOptions,
    showCampaignPackageOptionVariants,
  ]);

  return {
    /** Whether the campaign is available based on returned flight data */
    isCampaignAvailable,
    /** Whether we should show the campaign variants of package options
     *  (as opposed to 'Holidays Exclusive Deal') */
    showCampaignPackageOptionVariants,
    /** The room options with their matchingOffers (and hence availability)
     *  updated to reflect the availability of the campaign if we are showing
     *  the campaign package option variants*/
    campaignRoomOptions,
    /** The non-campaign (Holidays Exclusive) room option to present to users
     * as an alternative when there is no availability due to campaign flights */
    nonCampaignAltRoomOption,
  };
};

export default useFlightCampaignAvailability;
